import PersistentWebSocket from "../persistentWebSocket"
import { doFetch, doRawFetch } from "../api"
import makeStreamLineIterator from '../utils/streamLineReader'
import { writable } from "svelte/store"
import * as map from '../map'
import { protocol } from '../const'

export const EventTypes = {
	STEP: "handleFlightStep",
	HISTORY_APPEND: "handleFlightHistoryAppend",
	KEYFRAME: "handleKeyframe",
	REGISTERED: "handleRegistered"
}

export class FlightEventHandler {
	constructor(flight) {
		this.flight = flight
	}
	handleRegistered() {}
	handleFlightStep(_message) {}
	handleKeyframe(_keyframe) {}
	/**
	 * Called when a new message is put in the flight history.
	 * @param {FlightHistoryMessage} message The newly appended message
	 */
	handleFlightHistoryAppend(_message) { }

	cleanup() {}
}


export class Flight {
	serverState = {} // The actual information from the server
	segmentData = []
	eventHandlers = []
	requestedTime = window.location.hash ? new Date(window.location.hash.replace('#', '')) : null
	serverStateStore = writable(null)
	liveServerStateStore = writable(null)
	replayInfoStore = writable({
		flight: this,
		speed: 1,
		prevFrameTime: 0,
		accumulationStart: 0,
		currentTimestamp: 0,
		animationHandle: null,
		elapsed: 0,
		isPaused: false,
		isFollowingHead: true,
		currentLiveTimestamp: null,
	})
	selectedGRS = writable(null)
	segmentIndex = 0
	syncingToLive = true
	nextSegment = {}
	nextSegmentPromise = null
	lastFullKeyframeTime = 0
	lastSegment = []
	isGuest = false

	constructor(serverState, isGuest = false) { 
		this.isGuest = isGuest
		this.serverState = serverState
		this.serverStateStore.set(this.serverState)
		this.flightId = serverState.id

		this.overlay = map.addOverlay(serverState.name, null)

		if (this.isLive) {
			this.connect()
		}
		this.replayInfoStore.subscribe(value => {
			this.replayInfo = value
		})
		this.liveServerStateStore.subscribe(value => {
			this.liveServerState = value
		})
		this.liveServerStateStore.set(serverState)
		
		const params = new URLSearchParams(window.location.search)
		const t = params.get('t')
		if (t) {
			this.isReplayPaused = true
			this.loadPromise = this.seekToTime(parseInt(t))
		} else {
			this.loadPromise = this.loadKeyframe()
		}
	}

	async startup() {
		if (!this.loadPromise) return
		return this.loadPromise
	}

	get isLive() {
		return this.serverState.eventWebSocket !== undefined
	}

	get isReplayPaused() {
		return this.replayInfo.isPaused
	}
	set isReplayPaused(value) {
		this.replayInfoStore.update(old => {
			old.isPaused = value
			if (value) {

				this.replayInfoStore.update(old => {
					if (old.isFollowingHead) {
						this.segmentData = this.lastSegment
						this.segmentIndex = this.lastSegment.length - 1
					}
					old.isFollowingHead = false
					return old
				})
			}
			this.startStepping()
			return old
		})
	}

	get isConnected() {
		return this.liveWs
	}

	connect() {
		if (!this.isLive) {
			console.error("Tried to connect to an ended flight.")//TODO bug
			return
		}

		if (this.liveWs) {
			return
		}

		// Connect to the websocket for live data
		const url = protocol === "https:" ? this.serverState.eventWebSocket.replace("ws:", "wss:") : this.serverState.eventWebSocket
		this.liveWs = new PersistentWebSocket(url)
		this.liveWs.addEventListener("message", this.handleLiveWsMessage.bind(this))
		this.liveWs.connect()

		document.body.classList.add('flightFeedConnected')
	}

	disconnect() {
		this.liveWs?.close()
		delete this.liveWs
		document.body.classList.remove('flightFeedConnected')
	}

	getHandler(handlerClass) {
		let handlerInfo = this.eventHandlers.find(h => h.handler instanceof handlerClass)
		if (!handlerInfo) {
			handlerInfo = { handler: new handlerClass(this), count: 0 }
			this.registerEventHandler(handlerInfo.handler)
			this.eventHandlers.push(handlerInfo)
		}
		handlerInfo.count++
		return handlerInfo.handler
	}

	releaseHandler(handlerClass) {
		const i = this.eventHandlers.findIndex(h => h.handler instanceof handlerClass)
		if (i < 0) return
		const handlerInfo = this.eventHandlers[i]
		handlerInfo.count--
		if (handlerInfo.count <=0) {
			try {
				handlerInfo.handler.cleanup()
			} catch {}
			this.eventHandlers.splice(i, 1)
		}
	}

	registerEventHandler(handler) {
		for (const fn of Object.values(EventTypes)) {
			if (!handler[fn] || typeof handler[fn] !== "function") {
				console.error("Can not register the handler because it does not have the function ", fn)
				return
			}
		}

		handler[EventTypes.REGISTERED]()

		// we need to catch this handler back up to live
		if (this.keyframe) {
			try {
				handler[EventTypes.KEYFRAME](this.keyframe)
				for (const rawMsg of this.segmentData) {
					handler[EventTypes.STEP](rawMsg)
				}			
			} catch (error) {
				console.error('Could not sync flight event handler:', error)
			}
		}
	}

	dispatchEvent(eventType, data) {
		console.assert(eventType)
		for (const { handler } of this.eventHandlers) {
			try {
				handler[eventType](data)
			} catch (err) {
				console.error(`Handler ${typeof handler}.${eventType} threw an error:`, err)
			}
		}
	}

	handleLiveWsMessage(event) {
		// Display stubbed data if we disconnect
		if (!event || !event.detail) return
		
		let rawMsg
		try {
			rawMsg = JSON.parse(event.detail);
		} catch (err) {
			console.error("Got Invalid JSON on the live feed.", event)
			return
		}


		if (this.replayInfo.isFollowingHead || this.isReplayPaused) {
			this.replayInfoStore.update(old => {
				old.currentLiveTimestamp = rawMsg.timestamp
				return old
			})
		}

		if (rawMsg.type == "FLIGHT_STATE") {
			// console.log("Updating live state", rawMsg)
			// TODO:  Is this best?
			if (rawMsg.state === "ENDED" && this.isLive) {
				this.disconnect()
			}

			this.liveServerStateStore.set(rawMsg.flight)
		} else if (rawMsg.type == "FLIGHT_SYNC") {
			return //we no longer use this because keyframes
		} else if (rawMsg.type == "KEYFRAME") {
			if (this.isGuest) return
			// console.log('New keyframe', rawMsg.keyframeTimestamp)
			this.lastSegment = []
			if (!this.keyframe.nextKeyframeTime) this.keyframe.nextKeyframeTime = rawMsg.keyframeTimestamp
			this.lastFullKeyframeTime = rawMsg.keyframeTimestamp
		}

		this.lastSegment.push(rawMsg)
		this.dispatchEvent(EventTypes.HISTORY_APPEND, rawMsg)
		if (!this.replayInfo.isFollowingHead) return

		if (this.syncingToLive) {
			this.segmentData.push(rawMsg)
			return
		}

		this.processMessage(rawMsg)
		this.dispatchEvent(EventTypes.STEP, rawMsg)

		this.replayInfoStore.update(old => {
			old.currentTimestamp = rawMsg.timestamp
			return old
		})
	}

	processMessage(rawMsg) {
		if (rawMsg.type == "FLIGHT_STATE") {
			// console.log("Updating state", rawMsg)
			this.serverState = rawMsg.flight
			this.serverStateStore.set(rawMsg.flight)
		}
	}

	async loadKeyframe(timestamp=null) {
		if (!timestamp) {
			this.syncingToLive = true
			this.segmentData = []
		}
		this.keyframe = await this.getKeyframe(timestamp)
		this.serverState = this.keyframe.flightState
		this.serverStateStore.set(this.keyframe.flightState)
		this.dispatchEvent(EventTypes.KEYFRAME, this.keyframe)
		if (!timestamp) {
			this.syncingToLive = false
			for (const rawMsg of this.segmentData) {
				this.processMessage(rawMsg)
				this.dispatchEvent(EventTypes.STEP, rawMsg)
			}
			this.segmentData = []
		}
		
		this.replayInfoStore.update(old => {
			old.currentTimestamp = this.keyframe.end
			return old
		})
	}

	async getKeyframe(timestamp=null) {
		return await doFetch(`active_flight/${this.serverState.id}/keyframe?timestamp=${timestamp}`)
	}

	async getSegmentData(timestamp) {
		if (this.isGuest) return
		return (await doRawFetch(`active_flight/${this.flightId}/segment?timestamp=${timestamp}`)).body
	}

	goToLive() {
		this.replayInfoStore.update(old => {
			old.isFollowingHead = true
			return old
		})
		this.isReplayPaused = false
		if (this.replayInfo.animationHandle) {
			cancelAnimationFrame(this.replayInfo.animationHandle)
		}
		this.loadKeyframe()
	}

	startStepping() {
		if (this.replayInfo.animationHandle) {
			cancelAnimationFrame(this.replayInfo.animationHandle)
		}
		this.replayInfo.prevFrameTime = 0

		if (!this.isReplayPaused) {
			this.replayInfo.animationHandle = window.requestAnimationFrame(this.stepMessages.bind(this))
		}
	}

	_setSegmentIndex(i, timestamp) {
		this.segmentIndex = i < 0 ? 0 : i
		this.replayInfoStore.update(old => {
			old.currentTimestamp = timestamp
			return old
		})
		// console.log('segmentIndex', i, timestamp)
	}

	async loadNextSegment() {
		if (this.nextSegmentPromise || this.nextSegment.data) return
		if (!this.keyframe.nextKeyframeTime){
			console.error('nextKeyframeTime is not set when trying to load next segment')
			return
		}
		
		const promises = [
			this.getKeyframe(this.keyframe.nextKeyframeTime),
			this.getSegmentData(this.keyframe.nextKeyframeTime)
		]

		this.nextSegmentPromise = Promise.all(promises)
		let keyframe, data
		
		try {
			[keyframe, data] = await this.nextSegmentPromise
		} catch (error) {
			this.nextSegmentPromise = null
			return
		}

		if (this.nextSegment.cancel) {
			// console.log('cancelling next segment pull', this.keyframe.nextKeyframeTime)
			this.nextSegment = {}
			this.nextSegmentPromise = null
			return
		}

		let messages = []
		for await (let line of makeStreamLineIterator(data)) {
			try {
				if (!line || !line.length) continue
				const message = JSON.parse(line)
				messages.push(message)
			} catch (error) {
				console.error('Could not parse log line', line)
			}
		}

		if (!keyframe.nextKeyframeTime) {
			this.mergeDataWithLive(messages)
			messages = this.lastSegment
		}

		this.nextSegment = {
			keyframe,
			data: messages
		}
		this.nextSegmentPromise = null

		// console.log('next segment loaded', this.nextSegment, messages.length)

		this.startStepping()
	}

	mergeDataWithLive(messages) {
		if (!this.lastSegment || this.lastSegment.length === 0) {
			this.lastSegment = [...messages]
			return
		}

		const firstTimestamp = this.lastSegment[0].timestamp
		let i = messages.length - 1
		while (i > 0 && messages[i].timestamp > firstTimestamp) {
			i--
		}
		if (i < (messages.length - 1)) {
			// console.log("Removing overlap from the history", messages, i)
			messages.splice(i)
		}

		// Smash the history together
		// console.log("Combining the log and live data", messages[messages.length - 1], this.lastSegment[0])
		this.lastSegment = messages.concat(this.lastSegment)
	}

	async seekToTime(timestamp) {
		window.cancelAnimationFrame(this.replayInfo.animationHandle)
		
		this.replayInfoStore.update(old => {
			old.isFollowingHead = false
			return old
		})

		this.nextSegment = {
			cancel: true
		}

		const processToTimestamp = () => {
			let i = 0
			while (i < this.segmentData.length && this.segmentData[i].timestamp <= timestamp) {
				this.processMessage(this.segmentData[i])
				this.dispatchEvent(EventTypes.STEP, this.segmentData[i])
				i++
			}
			this._setSegmentIndex(i-1, timestamp)
		}

		if (this.segmentData && this.segmentData.length && this.segmentData[0].timestamp <= timestamp && this.segmentData[this.segmentData.length - 1].timestamp >= timestamp) {
			// console.log('replaying in current segment', this.segmentData.length)

			// we are already in the current keyframe/segment data
			this.dispatchEvent(EventTypes.KEYFRAME, this.keyframe)
			processToTimestamp()
		} else if (this.lastFullKeyframeTime > 0 && this.lastFullKeyframeTime < timestamp) {
			// We intentionally make this an object reference so live data appends to the segment
			// we know the lastSegment is complete since we saw the last keyframe come in.
			// console.log('setting segmentData to lastSegment', this.lastSegment.length)
			this.segmentData = this.lastSegment
			await this.loadKeyframe(timestamp)
			processToTimestamp()
		} else {
			const promises = [
				this.loadKeyframe(timestamp),
				this.getSegmentData(timestamp)
			]
	
			const [,data] = await Promise.all(promises)
			const messages = []
			for await (let line of makeStreamLineIterator(data)) {
				try {
					if (!line || !line.length) continue
					const message = JSON.parse(line)
					messages.push(message)
				} catch (error) {
					console.error('Could not parse log line', line)
				}
			}
			this.segmentData = messages

			if (!this.keyframe.nextKeyframeTime) {
				this.mergeDataWithLive(messages)
				this.segmentData = this.lastSegment
			}

			processToTimestamp()
		}
		// console.log('downloaded segment', this.segmentData.length)

		this.startStepping()
	}

	setReplaySpeed(speed) {
		window.cancelAnimationFrame(this.replayInfo.animationHandle)
		this.replayInfo.animationHandle = null

		this.replayInfoStore.update(old => {
			old.speed = Math.max(speed, 0.25)
			return old
		})

		if (!this.isReplayPaused) {
			this.replayInfo.animationHandle = window.requestAnimationFrame(this.stepMessages.bind(this))
		} else {
			this.replayInfo.prevFrameTime = 0
		}
	}

	stepMessages(timestamp) {
		if (this.isReplayPaused || this.replayInfo.stopped) {
			// console.log('replay stopped or paused', this.isReplayPaused, this.replayInfo.stopped)
			return
		}

		if (this.segmentIndex >= this.segmentData.length) {
			
			if (this.nextSegment && this.nextSegment.data) {
				this.keyframe = this.nextSegment.keyframe
				// TODO: should this be called?
				// this.dispatchEvent(EventTypes.KEYFRAME, this.keyframe)
				// console.log('Switching to next segment', this.nextSegment)
				this.segmentData = this.nextSegment.data
				this.segmentIndex = 0
				this.nextSegment = {}
			} else if (this.serverState.state === "ENDED") {				
				this.replayInfoStore.update(old => {
					old.isFollowingHead = true
					return old
				})
				// console.log('We are out of segments', this.serverState.state)
				return
			} else if (this.nextSegmentPromise) {
				// show spinner...
				// console.log('waiting for more data', this.nextSegmentPromise, this.nextSegment)
				return
			}
		}

		if (this.replayInfo.prevFrameTime === 0) {
			// Do our initial setup
			this.replayInfo.prevFrameTime = timestamp
			this.replayInfo.accumulationStart = this.replayInfo.currentTimestamp
			this.replayInfo.elapsed = 0
		}

		const frameTime = timestamp - this.replayInfo.prevFrameTime
		this.replayInfo.prevFrameTime = timestamp

		this.replayInfo.elapsed += frameTime * this.replayInfo.speed

		let lastTimestampToSend = this.replayInfo.accumulationStart + this.replayInfo.elapsed

		let lastSentTimestamp = lastTimestampToSend

		while (this.segmentIndex < this.segmentData.length && this.segmentData[this.segmentIndex].timestamp <= lastTimestampToSend) {
			const data = this.segmentData[this.segmentIndex]
			this.processMessage(data)
			this.dispatchEvent(EventTypes.STEP, data)
			// lastSentTimestamp = data.timestamp
			this.segmentIndex++
		}

		if (this.segmentIndex >= this.segmentData.length * 0.9 && !this.nextSegmentPromise && !this.nextSegment.data && this.keyframe.nextKeyframeTime) {
			// load next segment
			this.nextSegment = {}
			this.loadNextSegment()
		}

		this.replayInfoStore.update(old => {
			old.currentTimestamp = lastSentTimestamp
			if (this.isLive) {
				old.currentLiveTimestamp = Date.now()
			}
			return old
		})

		this.replayInfo.animationHandle = window.requestAnimationFrame(this.stepMessages.bind(this))
	}

	cleanup() {
		this.disconnect()
		
		if (this.replayInfo.animationHandle) window.cancelAnimationFrame(this.replayInfo.animationHandle)

		for (const { handler } of this.eventHandlers) {
			handler.cleanup()
		}

		if (this.overlay) {
			map.removeOverlay(this.overlay)
		}
	}
}